import ContentHeader from '../../components/UI/ContentHeader/ContentHeader';
import { MASTER_LINKS } from '../../constants/master-list-tabs';
import TherapyList from './TherapyList/TherapyList';
import TherapyService from '../../services/TherapyService';
import { ReactQueryKeys } from '../../constants/react-query-keys';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { SweetAlertResult } from 'sweetalert2';
import SwalAlert, {
  firePreConfirmAlert,
} from '../../components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from '../../constants/roles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Therapy } from '../../interfaces/therapy';
import Spinner from 'components/UI/Spinner/Spinner';
import HospitalTotalLabels from 'components/UI/HospitalTotalLabels/HospitalTotalLabels';
import { selectUser } from 'store/slices/auth';

/**
 * @description Therapies component that displays a list of therapies from the master catalog with their details.
 * Provides functionality for searching, pagination, and deletion of therapies with role-based access control.
 *
 * @SADtrace SAD-003-3.1, SAD-003-3.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} The rendered Therapies component
 */
const Therapies = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const user = useSelector(selectUser);
  // v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type
  const canManage = [ADMINISTRATION, AUTHOR].includes(user?.role || '');
  // v8 ignore stop -- WAIVER(MICRO)

  /**
   * @description Query to fetch therapies data with pagination and filtering
   */
  const {
    data: therapies,
    isLoading: isLoadingTherapies,
    isFetching,
  } = useQuery({
    queryKey: [
      ReactQueryKeys.THERAPIES,
      {
        columnFilters,
        pagination,
      },
    ],

    queryFn: () =>
      TherapyService.get({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
        columnFilters: columnFilters,
      }),

    placeholderData: keepPreviousData,
  });

  /**
   * @description Updates search filters for the API call to filter therapies by name
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string} value - The search value to filter therapies by name
   *
   * @returns {void}
   */
  const handleUpdateFilter = (value: string) => {
    setColumnFilters([
      {
        id: 'name',
        value,
      },
    ]);
  };

  /**
   * @description Shows confirmation alert for deleting a therapy record from the master catalog.
   * If confirmed, deletes the therapy and updates the query cache
   *
   * @SADtrace SAD-003-3.1, SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Therapy} therapy - The therapy object to be deleted
   *
   * @returns {void}
   */
  const handleDeleteShow = (therapy: Therapy) => {
    firePreConfirmAlert({
      title: t('therapies.messages.modal__delete__title'),
      html: t('therapies.messages.modal__delete__content', {
        name: therapy.name,
      }),
      preConfirm: () => {
        return TherapyService.delete(String(therapy.id))
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.THERAPIES],
        });
        navigate('/therapies');
        toast.success(t('therapies.messages.delete__success'));
      }
    });
  };

  return (
    <>
      <ContentHeader links={MASTER_LINKS} />
      <div className="main-container with-tabs">
        <div className="row">
          <div className="col-3">
            {isLoadingTherapies ? (
              <Spinner isAbsolute />
            ) : (
              <>
                <HospitalTotalLabels
                  total={therapies?.data.total}
                  translationKey="therapies"
                />
                <TherapyList
                  refetch={isLoadingTherapies || isFetching}
                  canManage={canManage}
                  onDelete={handleDeleteShow}
                  updateFilter={handleUpdateFilter}
                  therapyResponse={therapies?.data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            )}
          </div>
          <div className="col-9 ps-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default Therapies;
